"use client";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchPurchaseDetails, addPurchasePayment, reset } from "@/state/purchases/purchase.slice";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Loader2, ArrowLeft, DollarSign, Undo2, Pencil, Package, Receipt, History, Trash2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { usePermission } from "@/hooks/usePermission";
import { Link } from "react-router-dom";
import { format } from "date-fns";

const API_URL = import.meta.env.VITE_API_URL;

export default function PurchaseDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { toast } = useToast();
    const { currentPurchase, loading, success, error, message } = useSelector((state) => state.purchases);
    const { business } = useSelector((state) => state.business || {});
    const canAcceptPayments = usePermission('AcceptPayments');
    const canManagePayments = usePermission('ManagePayments');

    const [returns, setReturns] = useState([]);
    const [loadingReturns, setLoadingReturns] = useState(false);
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("Cash");
    const [editingPayment, setEditingPayment] = useState(null);
    const [deletePaymentId, setDeletePaymentId] = useState(null);
    const [isPaymentSubmitting, setIsPaymentSubmitting] = useState(false);

    const currency = business?.currency || "GHS";

    useEffect(() => {
        if (id) {
            dispatch(fetchPurchaseDetails(id));
            fetchReturns();
        }
    }, [id, dispatch]);

    useEffect(() => {
        if (success && message) {
            toast({ title: "Success", description: message });
            dispatch(reset());
            dispatch(fetchPurchaseDetails(id));
        }
        if (error) {
            toast({ variant: "destructive", title: "Error", description: error });
            dispatch(reset());
        }
    }, [success, error, message, toast, dispatch, id]);

    const fetchReturns = async () => {
        setLoadingReturns(true);
        try {
            const res = await axios.get(`${API_URL}purchases/returns?purchase_id=${id}`, {
                withCredentials: true,
            });
            setReturns(res.data?.data || []);
        } catch (err) {
            console.error("Failed to fetch returns:", err);
        } finally {
            setLoadingReturns(false);
        }
    };

    const handleAddPayment = async () => {
        if (!paymentAmount) return;
        await dispatch(addPurchasePayment({
            id,
            paymentData: {
                amount: parseFloat(paymentAmount),
                payment_method: paymentMethod,
            },
        }));
        setPaymentDialogOpen(false);
        setPaymentAmount("");
    };

    const handleEditPayment = (payment) => {
        setEditingPayment(payment);
        setPaymentAmount(payment.amount.toString());
        setPaymentMethod(payment.payment_method);
        setPaymentDialogOpen(true);
    };

    const handleUpdatePayment = async () => {
        if (!editingPayment || !paymentAmount) return;
        setIsPaymentSubmitting(true);
        try {
            await axios.put(`${API_URL}purchases/${id}/payments/${editingPayment.id}`, {
                amount: parseFloat(paymentAmount),
                payment_method: paymentMethod,
            }, { withCredentials: true });

            toast({ title: "Success", description: "Payment updated successfully" });
            setPaymentDialogOpen(false);
            setEditingPayment(null);
            setPaymentAmount("");
            dispatch(fetchPurchaseDetails(id));
        } catch (err) {
            toast({ variant: "destructive", title: "Error", description: err.response?.data?.message || "Failed to update payment" });
        } finally {
            setIsPaymentSubmitting(false);
        }
    };

    const handleDeletePayment = async () => {
        if (!deletePaymentId) return;
        setIsPaymentSubmitting(true);
        try {
            await axios.delete(`${API_URL}purchases/${id}/payments/${deletePaymentId}`, { withCredentials: true });
            toast({ title: "Success", description: "Payment deleted successfully" });
            setDeletePaymentId(null);
            dispatch(fetchPurchaseDetails(id));
        } catch (err) {
            toast({ variant: "destructive", title: "Error", description: err.response?.data?.message || "Failed to delete payment" });
        } finally {
            setIsPaymentSubmitting(false);
        }
    };

    if (loading || !currentPurchase) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    const purchase = currentPurchase;
    const total = parseFloat(purchase.total) || 0;
    const paid = parseFloat(purchase.amount_paid) || 0;
    const outstanding = Math.max(0, total - paid);

    const statusColor = (status) => {
        switch (status) {
            case "paid": return "bg-green-100 text-green-800";
            case "partial": return "bg-yellow-100 text-yellow-800";
            case "unpaid": return "bg-red-100 text-red-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    return (
        <div className="mx-auto p-4 space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Link to="/dashboard/purchases">
                        <Button variant="ghost" size="icon">
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                    </Link>
                    <div>
                        <h1 className="text-3xl font-bold">Purchase Details</h1>
                        <p className="text-muted-foreground">{purchase.invoice_number || `PO-${purchase.id}`}</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Link to={`/dashboard/purchases/edit/${id}`}>
                        <Button variant="outline">
                            <Pencil className="h-4 w-4 mr-2" /> Edit
                        </Button>
                    </Link>
                    <Link to={`/dashboard/purchases/returns-all/returns/add?purchase=${id}`}>
                        <Button variant="outline">
                            <Undo2 className="h-4 w-4 mr-2" /> Return Items
                        </Button>
                    </Link>
                    {canAcceptPayments && outstanding > 0 && (
                        <Button onClick={() => setPaymentDialogOpen(true)}>
                            <DollarSign className="h-4 w-4 mr-2" /> Add Payment
                        </Button>
                    )}
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="p-4">
                    <p className="text-sm text-muted-foreground">Supplier</p>
                    <p className="text-lg font-semibold">{purchase.Supplier?.name || "N/A"}</p>
                </Card>
                <Card className="p-4">
                    <p className="text-sm text-muted-foreground">Total Amount</p>
                    <p className="text-lg font-bold">{currency} {total.toFixed(2)}</p>
                </Card>
                <Card className="p-4">
                    <p className="text-sm text-muted-foreground">Amount Paid</p>
                    <p className="text-lg font-bold text-green-600">{currency} {paid.toFixed(2)}</p>
                </Card>
                <Card className="p-4">
                    <p className="text-sm text-muted-foreground">Outstanding</p>
                    <p className={`text-lg font-bold ${outstanding > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                        {currency} {outstanding.toFixed(2)}
                    </p>
                </Card>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="items" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="items" className="gap-2">
                        <Package className="h-4 w-4" /> Items
                    </TabsTrigger>
                    <TabsTrigger value="payments" className="gap-2">
                        <Receipt className="h-4 w-4" /> Payments
                    </TabsTrigger>
                    <TabsTrigger value="returns" className="gap-2">
                        <Undo2 className="h-4 w-4" /> Returns
                    </TabsTrigger>
                    <TabsTrigger value="details" className="gap-2">
                        <History className="h-4 w-4" /> Details
                    </TabsTrigger>
                </TabsList>

                {/* Items Tab */}
                <TabsContent value="items">
                    <Card>
                        <CardHeader>
                            <CardTitle>Purchased Items</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Product</TableHead>
                                        <TableHead>Quantity</TableHead>
                                        <TableHead>Unit Cost</TableHead>
                                        <TableHead className="text-right">Total</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(purchase.Stocks || []).map((stock, i) => (
                                        <TableRow key={i}>
                                            <TableCell className="font-medium">
                                                {stock.Product?.title || "Unknown Product"}
                                            </TableCell>
                                            <TableCell>{stock.quantity}</TableCell>
                                            <TableCell>{currency} {parseFloat(stock.data?.cost || 0).toFixed(2)}</TableCell>
                                            <TableCell className="text-right font-medium">
                                                {currency} {(stock.quantity * parseFloat(stock.data?.cost || 0)).toFixed(2)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Payments Tab */}
                <TabsContent value="payments">
                    <Card>
                        <CardHeader>
                            <CardTitle>Payment History</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {(purchase.payments || []).length === 0 ? (
                                <p className="text-center text-muted-foreground py-8">No payments recorded yet.</p>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Amount</TableHead>
                                            <TableHead>Method</TableHead>
                                            <TableHead>Paid By</TableHead>
                                            {canManagePayments && <TableHead className="text-right">Actions</TableHead>}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(purchase.payments || []).map((payment, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{format(new Date(payment.createdAt), "dd MMM yyyy HH:mm")}</TableCell>
                                                <TableCell className="font-medium">{currency} {parseFloat(payment.amount).toFixed(2)}</TableCell>
                                                <TableCell>{payment.payment_method}</TableCell>
                                                <TableCell>{payment.paidBy?.username || "N/A"}</TableCell>
                                                {canManagePayments && (
                                                    <TableCell className="text-right">
                                                        <div className="flex justify-end gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-7 w-7 p-0"
                                                                onClick={() => handleEditPayment(payment)}
                                                            >
                                                                <Pencil className="h-3 w-3" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                                                                onClick={() => setDeletePaymentId(payment.id)}
                                                            >
                                                                <Trash2 className="h-3 w-3" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Returns Tab */}
                <TabsContent value="returns">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle>Returns History</CardTitle>
                            <Link to={`/dashboard/purchases/returns-all/returns/add?purchase=${id}`}>
                                <Button size="sm">
                                    <Undo2 className="h-4 w-4 mr-2" /> Create Return
                                </Button>
                            </Link>
                        </CardHeader>
                        <CardContent>
                            {loadingReturns ? (
                                <div className="flex justify-center py-8">
                                    <Loader2 className="h-6 w-6 animate-spin" />
                                </div>
                            ) : returns.length === 0 ? (
                                <p className="text-center text-muted-foreground py-8">No returns for this purchase.</p>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Return #</TableHead>
                                            <TableHead>Items</TableHead>
                                            <TableHead>Total</TableHead>
                                            <TableHead>Status</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {returns.map((ret, i) => (
                                            <TableRow
                                                key={i}
                                                className="cursor-pointer hover:bg-muted/50"
                                                onClick={() => window.location.href = `/dashboard/purchases/returns-all/returns/${ret.id}`}
                                            >
                                                <TableCell>{format(new Date(ret.createdAt), "dd MMM yyyy")}</TableCell>
                                                <TableCell className="font-mono">{ret.return_number}</TableCell>
                                                <TableCell>{ret.items?.length || 0} items</TableCell>
                                                <TableCell>{currency} {parseFloat(ret.total || 0).toFixed(2)}</TableCell>
                                                <TableCell>
                                                    <Badge variant="outline">{ret.status?.toUpperCase()}</Badge>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Details Tab */}
                <TabsContent value="details">
                    <Card>
                        <CardHeader>
                            <CardTitle>Purchase Information</CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div>
                                <p className="text-sm text-muted-foreground">Invoice Number</p>
                                <p className="font-medium">{purchase.invoice_number || "-"}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Purchase Date</p>
                                <p className="font-medium">{format(new Date(purchase.createdAt), "dd MMM yyyy")}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Due Date</p>
                                <p className="font-medium">{purchase.due_date ? format(new Date(purchase.due_date), "dd MMM yyyy") : "-"}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Payment Status</p>
                                <Badge className={statusColor(purchase.payment_status)} variant="outline">
                                    {purchase.payment_status?.toUpperCase()}
                                </Badge>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Purchase Status</p>
                                <p className="font-medium capitalize">{purchase.purchase_status || "received"}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Discount</p>
                                <p className="font-medium">
                                    {purchase.discount_type !== "none"
                                        ? `${purchase.discount_value}${purchase.discount_type === "percentage" ? "%" : ` ${currency}`}`
                                        : "None"}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Tax Rate</p>
                                <p className="font-medium">{purchase.tax_rate || 0}%</p>
                            </div>
                            <div className="col-span-2">
                                <p className="text-sm text-muted-foreground">Notes</p>
                                <p className="font-medium">{purchase.notes || "-"}</p>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Add/Edit Payment Dialog */}
            <Dialog open={paymentDialogOpen} onOpenChange={(open) => {
                setPaymentDialogOpen(open);
                if (!open) {
                    setEditingPayment(null);
                    setPaymentAmount("");
                    setPaymentMethod("Cash");
                }
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingPayment ? "Edit Payment" : "Add Payment"}</DialogTitle>
                        <DialogDescription>
                            {editingPayment
                                ? `Editing payment of ${currency} ${parseFloat(editingPayment.amount).toFixed(2)}`
                                : `Outstanding: ${currency} ${outstanding.toFixed(2)}`}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Amount</Label>
                            <Input
                                type="number"
                                value={paymentAmount}
                                onChange={(e) => setPaymentAmount(e.target.value)}
                                max={outstanding}
                            />
                        </div>
                        <div>
                            <Label>Payment Method</Label>
                            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Cash">Cash</SelectItem>
                                    <SelectItem value="Momo">Mobile Money</SelectItem>
                                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                                    <SelectItem value="Check">Check</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
                        <Button
                            onClick={editingPayment ? handleUpdatePayment : handleAddPayment}
                            disabled={isPaymentSubmitting || !paymentAmount}
                        >
                            {isPaymentSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <DollarSign className="h-4 w-4 mr-2" />}
                            {editingPayment ? "Update Payment" : "Add Payment"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Payment Confirmation Dialog */}
            <Dialog open={!!deletePaymentId} onOpenChange={(open) => !open && setDeletePaymentId(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Payment</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this payment? This action cannot be undone and will restore the outstanding balance.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeletePaymentId(null)}>Cancel</Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeletePayment}
                            disabled={isPaymentSubmitting}
                        >
                            {isPaymentSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                            Delete Payment
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
